import type { DailyRoutineDetails } from '../dto/DailyRoutineDetails';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { Clock } from '../ports/Clock';
import type { DailyRoutineRepository } from '../ports/DailyRoutineRepository';
import type { DailyStepRepository } from '../ports/DailyStepRepository';
import type { EmotionalProtocolLogRepository } from '../ports/EmotionalProtocolLogRepository';
import type { IdGenerator } from '../ports/IdGenerator';
import type { RoutinePersistenceTransactions } from '../ports/RoutinePersistenceTransactions';
import type { RoutineStepTemplateRepository } from '../ports/RoutineStepTemplateRepository';
import { fromEmotionalStateData } from '../../domain/emotion/EmotionalStateContent';
import { calculateDailyProgress } from '../../domain/progression/CalculateDailyProgress';
import { applyModeVariant, selectActiveSteps } from '../../domain/routine/DayModeRules';
import { DayMode, TaskStatus } from '../../domain/routine/enums';
import type { DailyRoutine, DailyStep } from '../../domain/routine/models';
import { dayOfWeek } from '../../domain/shared/LogicalDate';
import type { DailyStepId, ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';

export class ChangeDailyMode {
  public constructor(
    private readonly dailyRoutines: DailyRoutineRepository,
    private readonly dailySteps: DailyStepRepository,
    private readonly routineStepTemplates: RoutineStepTemplateRepository,
    private readonly transactions: RoutinePersistenceTransactions,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
    private readonly emotionalProtocolLogs: EmotionalProtocolLogRepository,
  ) {}

  public async execute(
    profileId: ProfileId,
    logicalDate: string,
    mode: DayMode,
  ): Promise<Result<DailyRoutineDetails, RoutineError>> {
    try {
      const routine = await this.dailyRoutines.findByLogicalDate(profileId, logicalDate);
      if (!routine) return failure(new RoutineError('routine_not_found', 'Journée introuvable.'));
      if (routine.profileId !== profileId) {
        return failure(
          new RoutineError('mode_change_forbidden', 'Cette journée ne peut pas être modifiée.'),
        );
      }

      const timestamp = this.clock.now().toISOString();
      const steps = await this.dailySteps.listByRoutine(routine.id);
      const materializedExtras = await this.materializeModeOnlyExtras(
        routine,
        steps,
        mode,
        timestamp,
      );
      const reapplied = steps.map((step) => applyModeVariant(step, mode));
      const updatedSteps = [...reapplied, ...materializedExtras];

      const progress = calculateDailyProgress(selectActiveSteps(updatedSteps, mode));
      const updatedRoutine: DailyRoutine = {
        ...routine,
        dayMode: mode,
        completionPercentage: progress.percentage,
        updatedAt: timestamp,
      };

      await this.transactions.saveRoutineWithSteps(updatedRoutine, updatedSteps);
      const log = await this.emotionalProtocolLogs.findByLogicalDate(profileId, logicalDate);
      return success({
        routine: updatedRoutine,
        steps: updatedSteps,
        progress,
        emotionalState: (log && fromEmotionalStateData(log.data)?.state) ?? null,
      });
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }

  /**
   * Templates restricted to specific modes (e.g. the difficult-only water/breathing steps) have
   * no daily snapshot until their mode is selected. This creates them once, idempotently, without
   * touching any step that already exists for the day.
   */
  private async materializeModeOnlyExtras(
    routine: DailyRoutine,
    existingSteps: readonly DailyStep[],
    mode: DayMode,
    timestamp: string,
  ): Promise<readonly DailyStep[]> {
    if (!routine.templateId) return [];
    const templates = await this.routineStepTemplates.listByTemplate(routine.templateId);
    const existingTemplateStepIds = new Set(
      existingSteps.flatMap((step) => (step.templateStepId ? [step.templateStepId] : [])),
    );
    const weekday = dayOfWeek(routine.logicalDate);
    const missingTemplates = templates.filter(
      (template) =>
        template.active &&
        template.daysOfWeek.includes(weekday) &&
        (template.modes?.includes(mode) ?? false) &&
        !existingTemplateStepIds.has(template.id),
    );
    const startOrder = existingSteps.reduce((max, step) => Math.max(max, step.order), -1) + 1;
    return missingTemplates.map<DailyStep>((template, index) => ({
      id: this.ids.generate<DailyStepId>(),
      profileId: routine.profileId,
      dailyRoutineId: routine.id,
      templateStepId: template.id,
      title: template.title,
      category: template.category,
      status: TaskStatus.pending,
      order: startOrder + index,
      required: template.required,
      estimatedDurationMinutes: template.estimatedDurationMinutes,
      createdAt: timestamp,
      updatedAt: timestamp,
      ...(template.essential ? { essential: template.essential } : {}),
      ...(template.modes ? { modes: template.modes } : {}),
      ...(template.suggestedTime ? { suggestedTime: template.suggestedTime } : {}),
      data: template.content,
    }));
  }
}
